import { useEffect } from "react";
import { SHELL_APPS } from "../lib/apps";
import { INSTANCE } from "../lib/instance";
import { brandTitle } from "../../../shared/instance";

// =====================================================================
// useManifestSwitch — dynamisches PWA-Manifest pro Satellite-App
// =====================================================================
// Problem: Der Hub verwendet HashRouter. Alle Satellite-Apps laufen als
// Iframe innerhalb des Hub-HTML. Chrome liest das Manifest aus dem
// <link rel="manifest"> Tag des Top-Level-Dokuments. Da das immer das
// Hub-HTML ist, wird IMMER "Combsol Hub" als PWA-Installation angeboten.
//
// Loesung: Wenn eine Satellite-App aktiv ist, wird der <link rel="manifest">
// Tag dynamisch auf das Satellite-Manifest umgeschrieben. Chrome liest
// das Manifest bei jedem Seitenaufruf neu und bietet dann die korrekte
// App zur Installation an.
//
// Zusaetzlich werden <title>, <meta name="apple-mobile-web-app-title">
// und <meta name="theme-color"> aktualisiert, damit iOS-PWAs und
// Android-PWAs den korrekten App-Namen und die korrekte Farbe zeigen.
// =====================================================================

/** Mapping: appId → Manifest-URL und Meta-Daten */
const MANIFEST_MAP: Record<string, {
  href: string;
  title: string;
  themeColor: string;
}> = {};

// Dynamisch aus SHELL_APPS generieren
for (const app of SHELL_APPS) {
  if (app.kind === "satellite" && app.basePath) {
    MANIFEST_MAP[app.id] = {
      // Alle Satellite-Apps liefern manifest.webmanifest aus;
      // Fallback auf manifest.json fuer aeltere Apps.
      href: `${app.basePath}/manifest.webmanifest`,
      title: app.title,
      themeColor: "#0A0D11", // Default; wird ggf. aus dem Manifest ueberschrieben
    };
  }
}

// Spezifische Theme-Colors pro App (aus den jeweiligen Manifesten)
const THEME_COLORS: Record<string, string> = {
  fair:     "#0A0D11",
  liquid:   "#0A0D11",
  money:    "#07111d",
  notes:    "#0A0D11",
  briefing: "#0E1116",
  cases:    "#110800",
  life:     "#0A0D11",
};

// Hub-Defaults (werden beim Zurueckwechseln wiederhergestellt)
const HUB_MANIFEST = "/manifest.webmanifest";
const HUB_TITLE = `${brandTitle(INSTANCE)} Hub`;
const HUB_THEME_COLOR = "#0E1116";

function getOrCreateMeta(name: string): HTMLMetaElement {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.name = name;
    document.head.appendChild(el);
  }
  return el;
}

function getOrCreateManifestLink(): HTMLLinkElement {
  let el = document.querySelector('link[rel="manifest"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = "manifest";
    document.head.appendChild(el);
  }
  return el;
}

/**
 * Schaltet das PWA-Manifest, den Titel und die Theme-Color
 * dynamisch um, wenn eine Satellite-App aktiv wird.
 *
 * @param activeAppId - Die ID der aktuell aktiven App (z.B. "fair", "notes")
 */
export function useManifestSwitch(activeAppId: string): void {
  useEffect(() => {
    const satellite = MANIFEST_MAP[activeAppId];
    const manifestLink = getOrCreateManifestLink();
    const themeColorMeta = getOrCreateMeta("theme-color");
    const appleTitleMeta = getOrCreateMeta("apple-mobile-web-app-title");

    if (satellite) {
      // Satellite-App aktiv → Manifest umschalten
      manifestLink.href = satellite.href;
      document.title = satellite.title + " – " + brandTitle(INSTANCE);
      appleTitleMeta.content = satellite.title;
      themeColorMeta.content = THEME_COLORS[activeAppId] ?? satellite.themeColor;
    } else {
      // Hub-eigene View → Hub-Manifest wiederherstellen
      manifestLink.href = HUB_MANIFEST;
      document.title = HUB_TITLE;
      appleTitleMeta.content = "Hub";
      themeColorMeta.content = HUB_THEME_COLOR;
    }

    // Cleanup: Beim Unmount Hub-Defaults wiederherstellen
    return () => {
      manifestLink.href = HUB_MANIFEST;
      document.title = HUB_TITLE;
      appleTitleMeta.content = "Hub";
      themeColorMeta.content = HUB_THEME_COLOR;
    };
  }, [activeAppId]);
}
